import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ShieldCheck, EyeOff, Layers, Building2, Smartphone, FileText, Trash2, Lock, Globe, UserCheck, Camera, Folder, MapPin, Users, 
  PhoneCall, Bell, Calendar, Mic, Mail, ExternalLink, CheckCircle2, MessageSquare, Key
} from 'lucide-react';

const PrivacyPolicy = () => {
  const location = useLocation();

  const getNavClass = (path) => {
    const isActive = location.pathname === path;
    return `flex items-center px-5 py-3.5 rounded-2xl font-semibold transition-all duration-300 ${
      isActive 
        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25' 
        : 'text-gray-600 hover:bg-white/60 hover:text-gray-900 border border-transparent hover:shadow-sm'
    }`;
  };

  return (
    <div className="relative flex flex-col min-h-screen font-sans text-gray-800 bg-slate-100 md:flex-row overflow-hidden">
      
      {/* VIBRANT AMBIENT BACKGROUND ORBS */}
      <div className="absolute top-[-10%] left-[-5%] w-[45vw] h-[45vw] rounded-full bg-gradient-to-br from-blue-400/30 to-cyan-300/30 blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-5%] w-[40vw] h-[40vw] rounded-full bg-gradient-to-tr from-purple-400/25 to-pink-300/25 blur-[120px] pointer-events-none"></div>
      <div className="absolute top-[35%] left-[30%] w-[35vw] h-[35vw] rounded-full bg-gradient-to-r from-emerald-300/20 to-teal-300/20 blur-[100px] pointer-events-none"></div>

      {/* SHARED GLASS SIDEBAR */}
      <aside className="relative z-10 w-full bg-white/70 backdrop-blur-2xl border-b border-white/80 shadow-[4px_0_24px_rgba(0,0,0,0.03)] md:w-80 md:border-b-0 md:border-r md:min-h-screen flex flex-col">
        <div className="p-8 md:sticky md:top-0">
          <Link to="/" className="block mb-10 text-3xl font-black tracking-tighter text-gray-900 drop-shadow-sm">
            Expo<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Lead</span>
          </Link>
          <nav className="flex gap-3 pb-4 overflow-x-auto md:flex-col md:pb-0">
            <Link to="/help" className={getNavClass('/help')}>Help Center</Link>
            <Link to="/privacy-policy" className={getNavClass('/privacy-policy')}>Privacy Policy</Link>
            <Link to="/terms-and-conditions" className={getNavClass('/terms-and-conditions')}>Terms of Service</Link>
            <Link to="/refund-policy" className={getNavClass('/refund-policy')}>Refund Policy</Link>
          </nav>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="relative z-10 flex-1 p-4 sm:p-8 md:p-12 lg:p-16 overflow-y-auto">
        <div className="max-w-5xl mx-auto bg-white/80 backdrop-blur-2xl border border-white shadow-2xl shadow-indigo-950/5 rounded-[2.5rem] p-6 sm:p-10 md:p-14 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>

          {/* HERO HEADER */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-12 pb-8 border-b border-gray-100">
            <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-3xl shadow-xl shadow-indigo-500/30 text-white transform hover:rotate-6 transition-transform duration-300">
              <ShieldCheck className="w-11 h-11" />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 mb-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-blue-600" />
                Verified Legal Protocol & Meta Tech Provider Compliant
              </div>
              <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-gray-900">Privacy Policy</h1>
              <p className="text-gray-500 font-medium mt-1">Effective Date: July 20, 2026 | Last Updated: July 20, 2026</p>
            </div>
          </div>

          <div className="space-y-12 text-gray-700">
            
            {/* 1. INTRODUCTION */}
            <section className="p-8 bg-gradient-to-br from-blue-500/10 sm:from-blue-50 via-indigo-50/50 to-purple-50/50 rounded-3xl border border-blue-100/80 shadow-sm relative overflow-hidden group hover:shadow-md transition-all duration-300">
              <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-blue-500 to-indigo-600"></div>
              <h2 className="text-2xl font-black text-gray-900 mb-3 flex items-center gap-2.5">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600 text-white text-sm font-bold shadow-md">1</span>
                Introduction
              </h2>
              <p className="text-lg leading-relaxed text-gray-700 pl-1 sm:pl-10">
                <strong>NSENGINEERS</strong> ("we", "our", or "us") operates the <strong>EXPOLEAD</strong> mobile application. This Privacy Policy explains how we collect, use, disclose, store, and protect personal information when you use EXPOLEAD. By using the application, you agree to the practices described in this Privacy Policy.
              </p>
            </section>

            {/* 2. COMPANY INFORMATION */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-purple-100 text-purple-600 rounded-2xl shadow-sm"><Building2 className="w-6 h-6" /></div>
                <h2 className="text-2xl font-black text-gray-900">2. Company Information</h2>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-50 to-white border border-purple-100 shadow-sm hover:-translate-y-1 transition-all duration-300 group">
                  <span className="text-xs font-bold uppercase tracking-wider text-purple-600 block mb-1">Organization</span>
                  <strong className="text-xl text-gray-900 group-hover:text-purple-700 transition-colors">NSENGINEERS</strong>
                </div>

                <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-white border border-blue-100 shadow-sm hover:-translate-y-1 transition-all duration-300 group">
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-600 block mb-1">Application Name</span>
                  <strong className="text-xl text-gray-900 group-hover:text-blue-700 transition-colors">EXPOLEAD</strong>
                </div>

                <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm hover:-translate-y-1 transition-all duration-300 group">
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-600 block mb-1">Official Portal</span>
                  <a href="https://expolead.thensgroup.in" target="_blank" rel="noreferrer" className="text-lg font-bold text-gray-900 hover:text-emerald-600 inline-flex items-center gap-1.5 transition-colors">
                    expolead.thensgroup.in <ExternalLink className="w-4 h-4 text-emerald-500" />
                  </a>
                </div>

                <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-50 to-white border border-amber-100 shadow-sm hover:-translate-y-1 transition-all duration-300 group">
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-600 block mb-1">Support Desk</span>
                  <a href="mailto:expolead@thensgroup.in" className="text-lg font-bold text-gray-900 hover:text-amber-600 inline-flex items-center gap-1.5 transition-colors">
                    expolead@thensgroup.in <Mail className="w-4 h-4 text-amber-500" />
                  </a>
                </div>

                <div className="sm:col-span-2 p-6 rounded-2xl bg-gradient-to-r from-slate-50 via-gray-50 to-slate-50 border border-gray-200 shadow-sm flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-1">Registered Headquarters</span>
                    <strong className="text-lg text-gray-900">Ahmedabad, Gujarat, India</strong>
                  </div>
                  <MapPin className="w-8 h-8 text-gray-400 hidden sm:block" />
                </div>
              </div>
            </section>

            {/* 3 & 4. COLLECTION & USAGE */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <section className="p-8 rounded-3xl bg-white border border-gray-200/80 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-blue-100 text-blue-600 rounded-xl"><Layers className="w-5 h-5" /></div>
                    <h2 className="text-xl font-bold text-gray-900">3. Information We Collect</h2>
                  </div>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    We collect personal details to build your sales profile, including name, email address, mobile number, postal address, company name, profile photo, and CRM business entries.
                  </p>
                  <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100 text-sm text-gray-700 mb-6">
                    <strong>Third-Party OAuth Data:</strong> When connecting your WhatsApp Business Account via Facebook Login for Business, we programmatically collect and store temporary authorization codes, WhatsApp Business Account IDs (<code className="text-xs bg-white px-1 py-0.5 rounded border">waba_id</code>), Phone Number IDs (<code className="text-xs bg-white px-1 py-0.5 rounded border">phone_number_id</code>), and encrypted System-User Access Tokens.
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-blue-50/70 border border-blue-100 text-sm text-blue-900 font-medium flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse flex-shrink-0"></span>
                  Access to device sensors is strictly permission-gated and user-controlled.
                </div>
              </section>

              <section className="p-8 rounded-3xl bg-white border border-gray-200/80 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-indigo-100 text-indigo-600 rounded-xl"><FileText className="w-5 h-5" /></div>
                    <h2 className="text-xl font-bold text-gray-900">4. How We Use Information</h2>
                  </div>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    We process your information to authenticate users, manage sales leads, improve application performance, send reminders, provide customer support, and comply with legal obligations.
                  </p>
                  <div className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100 text-sm text-gray-700 mb-6">
                    <strong>Purpose-Scoped Routing:</strong> Meta OAuth tokens and identifiers are used strictly to deploy message templates and transmit automated follow-up communications to your scanned leads directly through your own telephone line. We do not read, process, or retain private chat histories outside of automated webhook delivery status logs.
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-indigo-50/70 border border-indigo-100 text-sm text-indigo-900 font-medium flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-indigo-600 animate-pulse flex-shrink-0"></span>
                  Your data is used solely to power your EXPOLEAD CRM workflows.
                </div>
              </section>
            </div>

            {/* 5. DEVICE PERMISSIONS */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-rose-100 text-rose-600 rounded-2xl shadow-sm"><Smartphone className="w-6 h-6" /></div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900">5. Device Permissions</h2>
                  <p className="text-sm text-gray-500">EXPOLEAD accesses specific device modules only when you trigger relevant features.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                
                <div className="p-5 rounded-2xl bg-white border border-rose-100 shadow-sm hover:shadow-md hover:border-rose-300 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><Camera className="w-5 h-5" /></div>
                  <h3 className="font-bold text-gray-900 text-base mb-1">Camera</h3>
                  <p className="text-sm text-gray-600">Capture photos and scan physical business documents/cards.</p>
                </div>

                <div className="p-5 rounded-2xl bg-white border border-amber-100 shadow-sm hover:shadow-md hover:border-amber-300 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><Folder className="w-5 h-5" /></div>
                  <h3 className="font-bold text-gray-900 text-base mb-1">Storage / Photos</h3>
                  <p className="text-sm text-gray-600">Upload, attach, and manage exported lead files and media.</p>
                </div>

                <div className="p-5 rounded-2xl bg-white border border-emerald-100 shadow-sm hover:shadow-md hover:border-emerald-300 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><MapPin className="w-5 h-5" /></div>
                  <h3 className="font-bold text-gray-900 text-base mb-1">Location</h3>
                  <p className="text-sm text-gray-600">Record accurate GPS locations for on-site client business visits.</p>
                </div>

                <div className="p-5 rounded-2xl bg-white border border-cyan-100 shadow-sm hover:shadow-md hover:border-cyan-300 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><Users className="w-5 h-5" /></div>
                  <h3 className="font-bold text-gray-900 text-base mb-1">Contacts</h3>
                  <p className="text-sm text-gray-600">Import device phonebook contacts directly into your lead funne.</p>
                </div>

                <div className="p-5 rounded-2xl bg-white border border-blue-100 shadow-sm hover:shadow-md hover:border-blue-300 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><PhoneCall className="w-5 h-5" /></div>
                  <h3 className="font-bold text-gray-900 text-base mb-1">Phone</h3>
                  <p className="text-sm text-gray-600">Initiate direct phone calls with prospects straight from lead records.</p>
                </div>

                <div className="p-5 rounded-2xl bg-white border border-purple-100 shadow-sm hover:shadow-md hover:border-purple-300 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><Bell className="w-5 h-5" /></div>
                  <h3 className="font-bold text-gray-900 text-base mb-1">Notifications</h3>
                  <p className="text-sm text-gray-600">Deliver real-time business alerts, follow-ups, and lead reminders.</p>
                </div>

                <div className="p-5 rounded-2xl bg-white border border-indigo-100 shadow-sm hover:shadow-md hover:border-indigo-300 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><Calendar className="w-5 h-5" /></div>
                  <h3 className="font-bold text-gray-900 text-base mb-1">Calendar</h3>
                  <p className="text-sm text-gray-600">Create, schedule, and sync client appointments initiated by you.</p>
                </div>

                <div className="sm:col-span-2 p-5 rounded-2xl bg-gradient-to-r from-teal-50/50 to-emerald-50/50 border border-teal-100 shadow-sm flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-teal-500 text-white flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-105 transition-transform"><Mic className="w-6 h-6" /></div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-base mb-0.5">SMS & Microphone</h3>
                    <p className="text-sm text-gray-600">Used strictly for voice note recording features and required business verification workflows where available.</p>
                  </div>
                </div>

              </div>
            </section>

            {/* 6. SHARING OF INFORMATION */}
            <section className="p-8 rounded-3xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white shadow-xl relative overflow-hidden">
              <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-blue-500/10 rounded-full blur-2xl"></div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-white/10 backdrop-blur-md rounded-xl text-rose-400 border border-white/10"><EyeOff className="w-6 h-6" /></div>
                <h2 className="text-2xl font-black">6. Sharing of Information</h2>
              </div>
              <p className="text-slate-300 text-lg leading-relaxed max-w-3xl">
                We <span className="text-white font-bold underline decoration-rose-500 decoration-2">do not sell or rent</span> personal information. We do not share personal information with advertisers. Information may be processed by authorized infrastructure providers (including Meta Platforms, Inc. for WhatsApp Cloud API routing) solely to operate the service or where required by law.
              </p>
            </section>

            {/* 7 & 8. RETENTION & DELETION */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <section className="p-8 rounded-3xl bg-gradient-to-br from-amber-50/80 to-orange-50/80 border border-amber-200/80 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-amber-500 text-white rounded-xl shadow-md"><Trash2 className="w-5 h-5" /></div>
                    <h2 className="text-xl font-bold text-gray-900">7. Data Retention</h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    We retain personal information while your account remains active. Upon account deletion, associated personal information is permanently deleted or anonymized unless retention is required by law.
                  </p>
                </div>
              </section>

              <section className="p-8 rounded-3xl bg-gradient-to-br from-rose-50/80 to-pink-50/80 border border-rose-200/80 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-rose-500 text-white rounded-xl shadow-md"><UserCheck className="w-5 h-5" /></div>
                    <h2 className="text-xl font-bold text-gray-900">8. Account Deletion & OAuth Revocation</h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Users may temporarily or permanently delete their account directly via our website portal at any time. You may also disconnect third-party OAuth integrations (such as WhatsApp Business) in your Profile Settings; revoking access scrubs all associated API access tokens and routing identifiers from production databases within 24 hours.
                  </p>
                </div>
              </section>
            </div>

            {/* 9. DATA SECURITY */}
            <section className="p-8 rounded-3xl bg-white border border-gray-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <Lock className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">9. Data Security</h2>
                <p className="text-gray-600 leading-relaxed">
                  We implement reasonable technical and organizational safeguards to protect information against unauthorized access, disclosure, alteration, and destruction. No transmission over the Internet is completely secure.
                </p>
              </div>
            </section>

            {/* 10. NEW: META TECH PROVIDER & WHATSAPP COMPLIANCE BANNER */}
            <section className="p-8 rounded-3xl bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 text-white shadow-xl border border-blue-500/30 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
              <div className="flex items-center gap-3 mb-6 border-b border-white/10 pb-4">
                <div className="p-2.5 bg-blue-500/20 rounded-xl text-blue-400 border border-blue-400/30"><MessageSquare className="w-6 h-6" /></div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-blue-400 block">Section 10</span>
                  <h2 className="text-2xl font-black text-white">Meta WhatsApp Cloud Services & Tech Provider Compliance</h2>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-slate-300 text-sm leading-relaxed">
                <div className="bg-white/5 p-5 rounded-2xl border border-white/10 backdrop-blur-sm">
                  <div className="flex items-center gap-2 font-bold text-white mb-2 text-base">
                    <Key className="w-4 h-4 text-blue-400" /> OAuth Asset Protection
                  </div>
                  <p>
                    When utilizing EXPOLEAD as a Meta Tech Provider solution, authentication is handled securely via Facebook Login for Business. System User Access Tokens generated during this handshake are encrypted at rest using industry-standard AES protocols and are never exposed to unauthorized internal or external parties.
                  </p>
                </div>

                <div className="bg-white/5 p-5 rounded-2xl border border-white/10 backdrop-blur-sm">
                  <div className="flex items-center gap-2 font-bold text-white mb-2 text-base">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Scope of Messaging Processing
                  </div>
                  <p>
                    We act strictly as a technical intermediary. Outbound messages are transmitted directly from your connected WhatsApp Business Account (<code className="text-xs bg-black/40 px-1 py-0.5 rounded text-blue-300">waba_id</code>). EXPOLEAD does not use your messaging channel to transmit platform advertising, nor do we mine message content for artificial intelligence model training.
                  </p>
                </div>
              </div>
            </section>

            {/* 11 - 14. ADDITIONAL LEGAL PROVISIONS */}
            <section className="space-y-4">
              <h2 className="text-2xl font-black text-gray-900 mb-6">Additional Legal Provisions</h2>
              
              <div className="p-6 rounded-2xl bg-white border border-gray-200/80 hover:border-blue-300 transition-colors shadow-sm flex gap-4">
                <Globe className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">11. International Users</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Data is stored in India. Users accessing the application from other jurisdictions acknowledge that their information may be transferred to and processed in India.</p>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-white border border-gray-200/80 hover:border-purple-300 transition-colors shadow-sm flex gap-4">
                <ShieldCheck className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">12. Children's Privacy</h3>
                  <p className="text-gray-600 text-sm sm:text-base">The application is intended for a general audience and is not designed to knowingly collect personal information from children where prohibited by law.</p>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-white border border-gray-200/80 hover:border-emerald-300 transition-colors shadow-sm flex gap-4">
                <UserCheck className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">13. Your Rights</h3>
                  <p className="text-gray-600 text-sm sm:text-base">Subject to applicable law, you may request access, correction, deletion, restriction, portability, or withdrawal of consent by contacting <a href="mailto:expolead@thensgroup.in" className="text-blue-600 font-bold hover:underline">expolead@thensgroup.in</a>.</p>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-white border border-gray-200/80 hover:border-amber-300 transition-colors shadow-sm flex gap-4">
                <FileText className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-1">14. Changes to this Privacy Policy</h3>
                  <p className="text-gray-600 text-sm sm:text-base">We may update this Privacy Policy periodically. Updates will be posted on our website with the revised Last Updated date.</p>
                </div>
              </div>
            </section>

            {/* 15. CONTACT US FOOTER BANNER */}
            <section className="p-8 sm:p-12 rounded-[2rem] bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-2xl shadow-indigo-500/25 flex flex-col sm:flex-row items-center justify-between gap-8 text-center sm:text-left relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
              <div className="relative z-10">
                <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-xs font-bold uppercase tracking-wider mb-3 inline-block"></span>
                <h3 className="font-black text-3xl mb-2">Have Questions About Your Data?</h3>
                <p className="text-blue-100 max-w-md text-base">Our privacy and engineering teams in Ahmedabad are ready to assist with any security inquiries or data rights requests.</p>
              </div>
              <a 
                href="mailto:expolead@thensgroup.in" 
                className="relative z-10 px-8 py-4 font-black text-gray-900 transition-all bg-white rounded-2xl hover:bg-blue-50 shadow-xl hover:scale-105 active:scale-95 whitespace-nowrap text-lg flex items-center gap-2"
              >
                <Mail className="w-5 h-5 text-blue-600" />
                expolead@thensgroup.in
              </a>
            </section>
          </div>

        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;